import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { fetchMarketplaceAppLogsApi } from '../../api/marketplaceApps.api';
import { Button } from '../../components/ui/Button';
import { useI18n } from '../../hooks/useI18n';

const DEFAULT_LOG_LINES = 500;

const takeLastLines = (value, maxLines = DEFAULT_LOG_LINES) => {
  const lines = String(value || '').replace(/\r\n/g, '\n').split('\n');
  return lines.slice(-maxLines).join('\n');
};

export const AppInstanceLogs = ({ instanceId }) => {
  const { t } = useI18n();
  const [logs, setLogs] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const logRef = useRef(null);
  const stickToBottomRef = useRef(true);

  const load = useCallback(async () => {
    if (!instanceId) {
      return;
    }

    try {
      const nextLogs = await fetchMarketplaceAppLogsApi(instanceId, DEFAULT_LOG_LINES);
      setLogs(takeLastLines(nextLogs || t('readyAppsLogsEmpty')));
      setError('');
    } catch (loadError) {
      setError(loadError?.message || t('readyAppsLogsError'));
    } finally {
      setLoading(false);
    }
  }, [instanceId, t]);

  useEffect(() => {
    setLoading(true);
    load();
    const timer = window.setInterval(load, 4000);
    return () => window.clearInterval(timer);
  }, [load, refreshNonce]);

  useEffect(() => {
    const node = logRef.current;
    if (!node || !stickToBottomRef.current) {
      return;
    }

    node.scrollTop = node.scrollHeight;
  }, [logs]);

  return (
    <section className="flex h-[min(62vh,640px)] min-h-[360px] flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-950">
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-neutral-200 px-4 py-3 dark:border-neutral-800">
        <div>
          <h2 className="text-sm font-semibold">{t('readyAppsLogsTitle')}</h2>
          <p className="mt-0.5 text-xs text-neutral-500">{t('readyAppsLogsHint')}</p>
        </div>
        <Button
          type="button"
          variant="ghost"
          className="h-8 px-2"
          onClick={() => {
            setLoading(true);
            setRefreshNonce((value) => value + 1);
          }}
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </Button>
      </div>
      {error ? (
        <p className="px-4 py-3 text-sm text-rose-600">{error}</p>
      ) : (
        <pre
          ref={logRef}
          dir="ltr"
          onScroll={(event) => {
            const node = event.currentTarget;
            stickToBottomRef.current = node.scrollHeight - node.scrollTop - node.clientHeight < 48;
          }}
          className="min-h-0 flex-1 overflow-auto whitespace-pre bg-neutral-50 px-4 py-3 text-left font-mono text-[12px] leading-5 text-neutral-700 dark:bg-neutral-900/50 dark:text-neutral-300"
        >
          {loading && !logs ? t('readyAppsLogsLoading') : logs}
        </pre>
      )}
    </section>
  );
};
